// Composant principal de la recherche avancée avec gestion des filtres, tris et champs affichés
import React, { useCallback, useEffect, useState, useMemo, useRef, type ReactNode } from "react";
// Helper pour comparer deux sections
import { FaFilter, FaBookmark, FaSave, FaSearch, FaTrash, FaCheck, FaTimes, FaSortAmountDown, FaColumns } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import U2000Tab from "../../tab/U2000Tab";
import U2000BasicSearchSort from "../commons/search/U2000BasicSearchSort";
import U2000BasicSearchField from "../commons/search/U2000BasicSearchField";
import U2000SearchFilterManagment from "../commons/search/U2000SearchFilterManagment";
import U2000BasicSearchCriteria, { type U2000BasicSearchCriteriaHandle } from "../commons/search/U2000BasicSearchCriteria";
import U2000SearchFilterSave, {
  type U2000SearchFilterSaveMode,
} from "../commons/search/U2000SearchFilterSave";
import U2000ActionBtn from "../../buttons/U2000ActionBtn";
import U2000KeyboardKeyHint from "../../common/U2000KeyboardKeyHint";
import type { TFunction } from "i18next";
import type { U2000SearchStateHandlers } from "../../../reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LogicalUnitContext } from "../../../context/logicalUnit/U2000LogicalUnitContext";
import { u2000DefaultSortOrderForColumn } from "../../../tools/U2000DefaultSortOrderForColumn";
import { u2000CoerceFilterLookupOperators } from "../../../tools/u2000SearchCriteriaFieldConditions";
import { u2000ShouldSkipSavedDefaultFilter } from "../../../tools/U2000QuickSearchUtils";
import { resolveCriteriaDisplayValue } from "../commons/search/U2000SearchPresenceCriteriaDisplay";
import { u2000IsAutocompleteAdvancedPanelOpen } from "../../../tools/u2000IsAutocompleteAdvancedPanelOpen";
import { u2000FilterHasDateCriteria, u2000HydrateStoredFilterDates, u2000SearchFiltersEquivalent } from "../../../tools/u2000DynamicSearchDateUtils";
import { u2000TodayOrgYmd } from "../../../tools/U2000DateTimeHelpers";

// Identifiants des onglets de recherche
export const SEARCHTAB = {
  CRITERIAS: "SEARCHTABCRITERIAS",
  SORT: "SEARCHTABSORT",
  FIELDS: "SEARCHTABFIELDS",
};

type PendingFilterUpdate = {
  id: string;
  name: string;
  isDefaultFilter: boolean;
};

// Utilisation d'un useState pour stocker les criterias
// Structure d'un filtre sauvegardé
export interface U2000SavedCriteria {
  id: string;
  name: string;
  isDefaultFilter?: boolean; // Indique si c'est le filtre par défaut
  section?: any; // Contient les critères, champs et tris associés à ce filtre
  [key: string]: any;
}

interface U2000BasicSearchProps {
  searchStateHandlers: U2000SearchStateHandlers;
  defaultTab: string;
  handleChangeTab: (tab: string) => void;
  handleShowSearch: (tab: string) => void;
  onFilterChange?(): void;
  /** Force le repli du panneau (ex. après Rechercher / F3) — BL-0407. */
  onCollapseSearch?(): void;
  /** Si true, affiche uniquement la barre compacte (icônes + puces + Rechercher). */
  isCollapsed?: boolean;
  /** Contenu affiché dans la barre compacte après les icônes (ex. préréglages LU). */
  collapsedLeadingSlot?: ReactNode;
  /** Si true, affiche les critères actifs comme puces retirables dans la barre compacte (défaut true — BL-0407). */
  showCollapsedCriteriaChips?: boolean;
  /** Appelé après suppression d’un critère via une puce (ex. resynchroniser un état LU). */
  onCriteriaChipRemoved?: (fieldName: string) => void;
  /** Onglets visibles dans le panneau (défaut : critères, tri, colonnes). */
  visibleSearchTabs?: string[];
}

//  Types pour les conditions de recherche
export interface U2000ConditionType {
  type: string;
  conditions: U2000Condition[];
}

export interface U2000Condition extends Record<string, unknown> {
  name: string;
  label: string;
  isRequiredValue: boolean;
}

function initConditionTypes(t: TFunction): U2000ConditionType[] {
  return [
    {
      type: "string",
      conditions: [
        {
          name: "equals",
          label: t("lblCndEquals"),
          isRequiredValue: true,
        },
        {
          name: "notequals",
          label: t("lblCndNotEquals"),
          isRequiredValue: true,
        },
        {
          name: "contains",
          label: t("lblCndContains"),
          isRequiredValue: true,
        },
        {
          name: "startswith",
          label: t("lblCndStartsWith"),
          isRequiredValue: true,
        },
        {
          name: "endswith",
          label: t("lblCndEndsWith"),
          isRequiredValue: true,
        },
        {
          name: "in",
          label: t("lblCndIn"),
          isRequiredValue: true,
        },
        {
          name: "isNull",
          label: t("lblCndIsNull"),
          isRequiredValue: false,
        },
        {
          name: "isNotNull",
          label: t("lblCndIsNotNull"),
          isRequiredValue: false,
        },
        {
          name: "interval",
          label: t("lblCndInterval"),
          isRequiredValue: true,
        },
      ],
    },
    {
      type: "number",
      conditions: [
        {
          name: "equals",
          label: t("lblCndEquals"),
          isRequiredValue: true,
        },
        {
          name: "notequals",
          label: t("lblCndNotEquals"),
          isRequiredValue: true,
        },
        {
          name: "greaterthan",
          label: t("lblCndGreaterThan"),
          isRequiredValue: true,
        },
        {
          name: "greaterthanorequal",
          label: t("lblCndGreaterThanOrEquals"),
          isRequiredValue: true,
        },
        {
          name: "lesthan",
          label: t("lblCndLessThan"),
          isRequiredValue: true,
        },
        {
          name: "lesthanorequal",
          label: t("lblCndLessThanOrEquals"),
          isRequiredValue: true,
        },
        {
          name: "isNull",
          label: t("lblCndIsNull"),
          isRequiredValue: false,
        },
        {
          name: "isNotNull",
          label: t("lblCndIsNotNull"),
          isRequiredValue: false,
        },
      ],
    },
    {
      type: "date",
      conditions: [
        {
          name: "equals",
          label: t("lblCndEquals"),
          isRequiredValue: true,
        },
        {
          name: "notequals",
          label: t("lblCndNotEquals"),
          isRequiredValue: true,
        },
        {
          name: "greaterthan",
          label: t("lblCndGreaterThan"),
          isRequiredValue: true,
        },
        {
          name: "greaterthanorequal",
          label: t("lblCndGreaterThanOrEquals"),
          isRequiredValue: true,
        },
        {
          name: "lesthan",
          label: t("lblCndLessThan"),
          isRequiredValue: true,
        },
        {
          name: "lesthanorequal",
          label: t("lblCndLessThanOrEquals"),
          isRequiredValue: true,
        },
        {
          name: "isNull",
          label: t("lblCndIsNull"),
          isRequiredValue: false,
        },
        {
          name: "isNotNull",
          label: t("lblCndIsNotNull"),
          isRequiredValue: false,
        },
        {
          name: "interval",
          label: t("lblCndInterval"),
          isRequiredValue: true,
        },
      ],
    },
    {
      type: "boolean",
      conditions: [
        {
          name: "equals",
          label: t("lblCndEquals"),
          isRequiredValue: true,
        },
        {
          name: "notequals",
          label: t("lblCndNotEquals"),
          isRequiredValue: true,
        },
      ],
    },
  ];
}

// Compare deux sections pour savoir si elles sont identiques (critères, champs, tris)
function isSectionEqual(a: any, b: any): boolean {
  return u2000SearchFiltersEquivalent(a, b, u2000TodayOrgYmd());
}

// Composant principal de la recherche (UI + logique)
const U2000BasicSearch: React.FC<U2000BasicSearchProps> = ({
  searchStateHandlers,
  defaultTab,
  handleChangeTab,
  handleShowSearch,
  onFilterChange,
  onCollapseSearch,
  isCollapsed = false,
  collapsedLeadingSlot,
  showCollapsedCriteriaChips = true,
  onCriteriaChipRemoved,
  visibleSearchTabs = [SEARCHTAB.CRITERIAS, SEARCHTAB.SORT, SEARCHTAB.FIELDS],
}) => {
  const { t } = useTranslation();
  const { t: tCommon, i18n } = useTranslation("common");
  const { quickSearchData, quickSearchSkipDefaultFilter } = useU2000LogicalUnitContext();
  const skipSavedDefaultFilterRef = useRef(false);
  if (u2000ShouldSkipSavedDefaultFilter(Boolean(quickSearchData), quickSearchSkipDefaultFilter)) {
    skipSavedDefaultFilterRef.current = true;
  }
  const [activeTab, setActiveTab] = useState<string>(defaultTab ?? "");
  const [defaultFilter, setDefaultFilter] = useState(false); // utilisé pour la boîte de dialogue de sauvegarde des filtres

  // ...existing code...
  // Dialog state for filter management
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [saveDialogMode, setSaveDialogMode] = useState<U2000SearchFilterSaveMode>("create");
  const [pendingFilterUpdate, setPendingFilterUpdate] = useState<PendingFilterUpdate | null>(null);

  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const criteriaFlushRef = useRef<U2000BasicSearchCriteriaHandle>(null);

  const conditionTypes = useMemo(() => initConditionTypes(t), [t]);
  const locale = i18n.language;
  const savedFilterCount = searchStateHandlers.getSavedFilters()?.length ?? 0;

  const applyCurrentFilter = (filter: Parameters<U2000SearchStateHandlers["setCurrentFilter"]>[0]) => {
    const hydrated = u2000HydrateStoredFilterDates(filter, u2000TodayOrgYmd());
    searchStateHandlers.setCurrentFilter(u2000CoerceFilterLookupOperators(hydrated, conditionTypes, locale));
  };

  useEffect(() => {
    if (hasInitialized) return;
    const savedFilters = searchStateHandlers.getSavedFilters() ?? [];
    if (!searchStateHandlers.hasAlreadyLoadedFilters) {
      // Quick Search : ignorer le filtre par défaut (ne pas AND, ne pas écraser un critère QS déjà posé)
      if (skipSavedDefaultFilterRef.current) {
        searchStateHandlers.selectFilterId(null);
        if (searchStateHandlers.getCriterias().length === 0) {
          // Pas encore de critère QS : colonnes/tri défaut seulement, critères vides
          searchStateHandlers.setFields(
            searchStateHandlers
              .getColumns()
              .filter((col: any) => col.isDefaultColumn)
              .map((col: any) => ({ field: { ...col } }))
          );
          searchStateHandlers.setSortedFields(
            searchStateHandlers
              .getColumns()
              .filter((col: any) => col.isDefaultSortedColumn)
              .map((col: any) => ({ field: { ...col }, sortOrder: u2000DefaultSortOrderForColumn(col) })),
          );
        }
      } else {
        // initialiser le filtre
        const defaultFilter = savedFilters.find((c) => c.isDefaultFilter);
        if (defaultFilter) {
          applyCurrentFilter(defaultFilter.filter);
          searchStateHandlers.selectFilterId(defaultFilter.id as string);
          if (searchStateHandlers.getLaunchSearch()) {
            setHasSearched(true);
          }
        } else {
          // Pas de setCriterias([]) ici : si l’UL a déjà des critères (ex. U3129 semaine courante avant
          // le 1er montage de BasicSearch — BasicTable ne rend la recherche qu’après son init), les conserver.
          // Liste vide : l’état est déjà sans critère.
          searchStateHandlers.setFields(
            searchStateHandlers
              .getColumns()
              .filter((col: any) => col.isDefaultColumn)
              .map((col: any) => ({ field: { ...col } }))
          );
          // Same wrapping for sorted fields
          searchStateHandlers.setSortedFields(
            searchStateHandlers
              .getColumns()
              .filter((col: any) => col.isDefaultSortedColumn)
              .map((col: any) => ({ field: { ...col }, sortOrder: u2000DefaultSortOrderForColumn(col) })),
          );
          searchStateHandlers.selectFilterId(null);
        }
      }
    }
    setHasInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init une fois ; savedFilters via reducer après localStorage
  }, [hasInitialized, quickSearchData, quickSearchSkipDefaultFilter]);

  useEffect(() => {
    if (!hasInitialized || searchStateHandlers.hasAlreadyLoadedFilters || skipSavedDefaultFilterRef.current) return;
    const savedFilters = searchStateHandlers.getSavedFilters() ?? [];
    if (savedFilters.length === 0 || searchStateHandlers.getSelectedFilterId()) return;
    const defaultFilter = savedFilters.find((c) => c.isDefaultFilter);
    if (!defaultFilter) return;
    applyCurrentFilter(defaultFilter.filter);
    searchStateHandlers.selectFilterId(defaultFilter.id as string);
    if (searchStateHandlers.getLaunchSearch()) {
      setHasSearched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applique le filtre par défaut après sync localStorage
  }, [hasInitialized, savedFilterCount, quickSearchData, quickSearchSkipDefaultFilter]);

  // Désactive le bouton de sauvegarde si aucun critère/champ/tri n'est sélectionné
  function isDisabledSaveFilter(): boolean {
    // Désactive si aucun critère, champ ou tri non default
    // On considère "default" comme les colonnes avec isDefaultColumn ou isDefaultSortedColumn
    const defaultFields = searchStateHandlers
      .getColumns()
      .filter((col: any) => col.isDefaultColumn)
      .map((col: any) => col.name);
    const defaultSortedFields = searchStateHandlers
      .getColumns()
      .filter((col: any) => col.isDefaultSortedColumn)
      .map((col: any) => col.name);

    // Si au moins un critère
    if (searchStateHandlers.getCriterias().length > 0) {
      return false;
    }

    // Si au moins un champ affiché qui n'est pas un champ par défaut
    const fields = searchStateHandlers.getFields().map((f: any) => (typeof f.field === "string" ? f.field : f.field?.name ?? "")) as string[];
    if (import.meta.env.DEV) console.debug("Fields:", fields, "DefaultFields:", defaultFields);
    const hasNonDefaultField = fields.some((name) => !defaultFields.includes(name));
    if (hasNonDefaultField || fields.length !== defaultFields.length) {
      return false;
    }

    // Si au moins un tri affiché qui n'est pas un tri par défaut
    const sortedFields = searchStateHandlers.getSortedFields().map((s: any) => (typeof s.field === "string" ? s.field : s.field?.name ?? "")) as string[];
    const hasNonDefaultSortedField = sortedFields.some((name) => !defaultSortedFields.includes(name));
    if (hasNonDefaultSortedField || sortedFields.length !== defaultSortedFields.length) {
      return false;
    }

    // Sinon, tout est par défaut, donc disabled
    return true;
  }

  // Réinitialise tous les critères et restaure les colonnes par défaut
  function doClearCriterias(): void {
    searchStateHandlers.setCriterias([]);
    // Wrap TableColumn into FieldItem { field: TableColumn } for the handlers expecting FieldItem[]
    searchStateHandlers.setFields(
      searchStateHandlers
        .getColumns()
        .filter((col: any) => col.isDefaultColumn)
        .map((col: any) => ({ field: { ...col } }))
    );
    // Same wrapping for sorted fields
    searchStateHandlers.setSortedFields(
      searchStateHandlers
        .getColumns()
        .filter((col: any) => col.isDefaultSortedColumn)
        .map((col: any) => ({ field: { ...col }, sortOrder: u2000DefaultSortOrderForColumn(col) })),
    );
    searchStateHandlers.selectFilterId(null);
  }

  // Sélectionne un filtre sauvegardé et applique ses critères
  // (la recherche est lancée après la mise à jour du state)
  function onSelectFilter(id: string | number): void {
    const selected = searchStateHandlers.getSavedFilters().find((c) => c.id === id);
    if (selected) {
      searchStateHandlers.selectFilterId(id as string);
      setHasSearched(true);
    }
  }

  // Définition des onglets de recherche (Critères, Tri, Champs)
  const allSearchTabs = [
    {
      id: SEARCHTAB.CRITERIAS,
      label: t("tabCriteriaFields"),
      content: (
        <U2000BasicSearchCriteria
          ref={criteriaFlushRef}
          searchStateHandlers={searchStateHandlers}
          filterKey={searchStateHandlers.getSelectedFilterId() ?? "nonFilter"}
          conditionTypes={conditionTypes}
        />
      ),
    },
    {
      id: SEARCHTAB.SORT,
      label: t("tabSortedFields"),
      content: <U2000BasicSearchSort searchStateHandlers={searchStateHandlers} />,
    },
    {
      id: SEARCHTAB.FIELDS,
      label: t("tabFields"),
      content: <U2000BasicSearchField searchStateHandlers={searchStateHandlers} />,
    },
  ];
  const myTabs = allSearchTabs.filter((tab) => visibleSearchTabs.includes(tab.id));

  // Ouvre la boîte de dialogue pour sauvegarder un filtre
  function handleSaveFilter() {
    if ((searchStateHandlers.getSavedFilters()?.length ?? 0) === 0) {
      setDefaultFilter(true);
    } else {
      setDefaultFilter(false);
    }
    setSaveDialogMode("create");
    setPendingFilterUpdate(null);
    setDialogOpen(true);
  }

  function handleUpdateFilterClick(filter: U2000SavedCriteria): void {
    const hasDates = u2000FilterHasDateCriteria(searchStateHandlers.getCurrentFilter());
    if (!hasDates) {
      searchStateHandlers.updateExistingFilter(String(filter.id), filter.name, !!filter.isDefaultFilter, false, {
        makeDatesDynamic: false,
      });
      setHasSearched(true);
      return;
    }
    setSaveDialogMode("update");
    setPendingFilterUpdate({
      id: String(filter.id),
      name: filter.name,
      isDefaultFilter: !!filter.isDefaultFilter,
    });
    setDialogOpen(true);
  }

  function onCloseSaveSearchFilter(result: {
    filterName: string;
    isDefaultFilter: boolean;
    makeDatesDynamic: boolean;
  }): void {
    const options = { makeDatesDynamic: result.makeDatesDynamic };
    if (saveDialogMode === "update" && pendingFilterUpdate) {
      searchStateHandlers.updateExistingFilter(
        pendingFilterUpdate.id,
        pendingFilterUpdate.name,
        pendingFilterUpdate.isDefaultFilter,
        false,
        options,
      );
      setPendingFilterUpdate(null);
    } else {
      searchStateHandlers.saveFilter(result.filterName, result.isDefaultFilter, options);
    }
    setHasSearched(true);
  }

  // Met à jour un filtre existant (nom, statut par défaut, section)
  function onFilterUpdate(id: string, filterName: string, isDefaultFilter: boolean, isFromFilterManagement: boolean) {
    searchStateHandlers.updateExistingFilter(id, filterName, isDefaultFilter, isFromFilterManagement);
  }

  // Supprime un filtre sauvegardé
  function onFilterDelete(id: string) {
    // const updatedCriterias = searchState.saveCriterias.filter((c) => c.id !== id);
    // updateSaveFilter(updatedCriterias);
    searchStateHandlers.deleteFilter(id);
  }

  useEffect(() => {
    if (hasSearched) {
      doSearch();
    }
  }, [hasSearched]);

  // Exécute la recherche avec la liste courante des critères, champs et tris.
  // BL-0407 — toute recherche (bouton, F3, initLU filtre défaut, sélection filtre) replie le panneau.
  function doSearch(): void {
    setHasSearched(false);
    onCollapseSearch?.();
    if (onFilterChange) {
      onFilterChange();
    }
  }

  const handleSearchClick = useCallback((): void => {
    criteriaFlushRef.current?.flushDraftCriteriaOnSearch();
    setHasSearched(true);
  }, []);

  /** Synchronise l’onglet actif avec le parent pour que le bouton ✕ et le toggle replient correctement. */
  function handleTabChange(id: string): void {
    setActiveTab(id);
    handleChangeTab(id);
  }

  // BL-0407 — F3 lance la recherche liste ; priorité au panneau AutoComplete F8 s’il est ouvert.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "F3") {
        return;
      }
      if (u2000IsAutocompleteAdvancedPanelOpen()) {
        return;
      }
      e.preventDefault();
      handleSearchClick();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSearchClick]);

  if (!hasInitialized) return;

  const rawSaved = searchStateHandlers.getSavedFilters();
  const sortedSavedFilters =
    rawSaved && rawSaved.length > 0
      ? [...rawSaved].sort((a: U2000SavedCriteria, b: U2000SavedCriteria) => Number(!!b.isDefaultFilter) - Number(!!a.isDefaultFilter))
      : [];

  const selectedFilterId = searchStateHandlers.getSelectedFilterId();
  const selectedSavedFilter = selectedFilterId
    ? sortedSavedFilters.find((f) => f.id === selectedFilterId)
    : undefined;
  const selectedFilterUnchanged =
    !!selectedSavedFilter?.filter &&
    isSectionEqual(searchStateHandlers.getCurrentFilter(), selectedSavedFilter.filter);

  // BL-0407 — si un filtre sauvegardé est sélectionné et non modifié, ne pas doubler avec les puces critères
  // (sinon « Employé actif » + « Statut actif: Vrai » pour un seul filtre par défaut).
  const showCriteriaChipsInCollapsedBar =
    showCollapsedCriteriaChips && !(selectedSavedFilter && selectedFilterUnchanged);

  const activeCriteriasForChips = showCriteriaChipsInCollapsedBar ? searchStateHandlers.getCriterias() : [];

  const criteriaChips =
    activeCriteriasForChips.length > 0
      ? activeCriteriasForChips.map((c) => {
          const chipValue = resolveCriteriaDisplayValue(c.field, c.condition, c.value, c.displayValue, tCommon, c.dynamicDate);
          return (
          <React.Fragment key={c.id}>
            <span className="flex items-center shrink-0 gap-1">
              <span
                className="max-w-[min(280px,55vw)] truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-800"
                title={`${c.field.label ?? c.field.name}: ${chipValue}`}
              >
                {c.field.label ?? c.field.name}: {chipValue}
              </span>
              <button
                type="button"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-500 transition-all duration-200 hover:bg-red-100 hover:text-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-500/20 active:scale-95"
                onClick={(e) => {
                  e.stopPropagation();
                  // BL-0428 — barre repliée : X = retirer + F3 ; dernier critère = reset poubelle (pas de search)
                  if (isCollapsed && searchStateHandlers.getCriterias().length <= 1) {
                    doClearCriterias();
                    return;
                  }
                  searchStateHandlers.deleteCriterias(c.field.name);
                  onCriteriaChipRemoved?.(c.field.name);
                  if (isCollapsed) {
                    handleSearchClick();
                  }
                }}
                title={t("criteriaChipRemove", { ns: "common", defaultValue: "Retirer ce critère" })}
                aria-label={t("criteriaChipRemove", { ns: "common", defaultValue: "Retirer ce critère" })}
              >
                <FaTimes className="text-xs" aria-hidden />
              </button>
            </span>
            <div className="mx-2 h-4 w-px shrink-0 bg-gray-300" aria-hidden />
          </React.Fragment>
          );
        })
      : null;

  const savedFilterChips = sortedSavedFilters.map((filter: U2000SavedCriteria) => {
    const isSelected = searchStateHandlers.getSelectedFilterId() === filter.id;
    let sectionChanged = false;
    if (isSelected && filter.filter) {
      sectionChanged = !isSectionEqual(searchStateHandlers.getCurrentFilter(), filter.filter);
    }
    return (
      <React.Fragment key={filter.id}>
        <span className="flex items-center shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onSelectFilter(filter.id);
            }}
            className={`px-3 py-1.5 text-sm transition-all duration-200 ease-in-out
                      rounded-lg border border-transparent
                      focus:outline-hidden focus:ring-2 focus:ring-blue-500/20
                      hover:bg-gray-100
                      ${filter.isDefaultFilter ? "font-semibold text-gray-900" : "font-medium text-gray-700"}
                      ${isSelected ? "bg-blue-50 border-blue-200 text-blue-900 shadow-xs" : "hover:text-gray-900"}`}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            aria-label={filter.isDefaultFilter ? `${filter.name} (filtre par défaut)` : filter.name}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectFilter(filter.id);
              }
            }}
          >
            {filter.name}
          </button>
          {isSelected && sectionChanged && (
            <>
              <button
                title="Sauvegarder les modifications dans ce filtre"
                className="ml-2 flex items-center justify-center h-7 w-7 rounded-full
                         text-green-600 hover:text-white hover:bg-green-600
                         transition-all duration-200 ease-in-out
                         focus:outline-hidden focus:ring-2 focus:ring-green-500/20
                         active:scale-95"
                onClick={() => {
                  handleUpdateFilterClick(filter);
                }}
              >
                <FaCheck className="text-xs" />
              </button>
              <button
                title="Annuler les modifications et restaurer le filtre"
                className="ml-1 flex items-center justify-center h-7 w-7 rounded-full
                         text-red-600 hover:text-white hover:bg-red-600
                         transition-all duration-200 ease-in-out
                         focus:outline-hidden focus:ring-2 focus:ring-red-500/20
                         active:scale-95"
                onClick={() => {
                  searchStateHandlers.restoreCurrentFilter();
                  setHasSearched(true);
                }}
              >
                <FaTimes className="text-xs" />
              </button>
            </>
          )}
        </span>
        <div className="mx-2 h-4 w-px bg-gray-300 shrink-0" aria-hidden />
      </React.Fragment>
    );
  });

  const searchSectionId = "u2000-basic-search-section";

  const collapsedTabIconBtnClass =
    "flex shrink-0 items-center justify-center h-9 w-9 text-gray-700 hover:text-gray-900 rounded-lg transition-all duration-200 ease-in-out hover:bg-gray-100 focus:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 active:bg-gray-200 active:scale-95";

  const searchBtnTrailingHint = <U2000KeyboardKeyHint>F3</U2000KeyboardKeyHint>;

  return (
    <>
      <U2000SearchFilterManagment
        searchStateHandlers={searchStateHandlers}
        open={openFilterDialog}
        onClose={() => setOpenFilterDialog(false)}
        onFilterUpdate={onFilterUpdate}
        onFilterDelete={onFilterDelete}
      />

      <U2000SearchFilterSave
        isDialogOpen={isDialogOpen}
        defaultFilter={defaultFilter}
        hasDateCriteria={u2000FilterHasDateCriteria(searchStateHandlers.getCurrentFilter())}
        mode={saveDialogMode}
        initialFilterName={pendingFilterUpdate?.name ?? ""}
        setDialogOpen={setDialogOpen}
        onClose={onCloseSaveSearchFilter}
      />

      {isCollapsed ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2 w-full" role="region" aria-label={t("searchPanelReducedBarAria")}>
          {/* BL-0407 — 3 icônes (filtrées par visibleSearchTabs) remplacent le chevron */}
          <div className="flex shrink-0 items-center gap-0.5" role="group" aria-label={t("searchPanelExpandAria")}>
            {visibleSearchTabs.includes(SEARCHTAB.CRITERIAS) ? (
              <button
                type="button"
                className={collapsedTabIconBtnClass}
                onClick={() => handleShowSearch(SEARCHTAB.CRITERIAS)}
                aria-expanded={false}
                aria-controls={searchSectionId}
                aria-label="Afficher les critères de recherche"
                title="Critères de recherche"
              >
                <FaFilter className="text-sm" aria-hidden />
              </button>
            ) : null}
            {visibleSearchTabs.includes(SEARCHTAB.SORT) ? (
              <button
                type="button"
                className={collapsedTabIconBtnClass}
                onClick={() => handleShowSearch(SEARCHTAB.SORT)}
                aria-expanded={false}
                aria-controls={searchSectionId}
                aria-label="Afficher les options de tri"
                title="Options de tri"
              >
                <FaSortAmountDown className="text-sm" aria-hidden />
              </button>
            ) : null}
            {visibleSearchTabs.includes(SEARCHTAB.FIELDS) ? (
              <button
                type="button"
                className={collapsedTabIconBtnClass}
                onClick={() => handleShowSearch(SEARCHTAB.FIELDS)}
                aria-expanded={false}
                aria-controls={searchSectionId}
                aria-label="Afficher la sélection des colonnes"
                title="Sélection des colonnes"
              >
                <FaColumns className="text-sm" aria-hidden />
              </button>
            ) : null}
          </div>

          {/* pl-2 minimum : garde une marge anti-rognage (bordure + focus ring) tout en rapprochant les puces des icônes */}
          <div className="flex-1 min-w-0 overflow-x-auto pl-2 pr-2">
            <div className="flex min-h-9 w-max flex-row flex-nowrap items-center justify-start py-0.5">
              {collapsedLeadingSlot ? (
                <>
                  <span className="flex shrink-0 flex-nowrap items-center gap-1">{collapsedLeadingSlot}</span>
                  <div className="mx-2 h-4 w-px shrink-0 bg-gray-300" aria-hidden />
                </>
              ) : null}
              {savedFilterChips}
              {criteriaChips}
            </div>
          </div>

          <div className="shrink-0 flex items-center justify-end sm:ml-auto">
            <U2000ActionBtn label={t("btnSearch")} icon={FaSearch} handleClick={handleSearchClick} trailingHint={searchBtnTrailingHint} />
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center">
            {(() => {
              const disabled = isDisabledSaveFilter();

              return (
                <div className="flex items-center gap-1">
                  <button
                    className="flex items-center justify-center h-9 w-9 text-gray-700 hover:text-gray-900
                          rounded-lg transition-all duration-200 ease-in-out
                          hover:bg-gray-100 focus:bg-gray-100
                          focus:outline-hidden focus:ring-2 focus:ring-blue-500/20
                          active:bg-gray-200 active:scale-95"
                    onClick={() => setOpenFilterDialog(true)}
                    title={t("dlgCriteriasManageFilterTitle", { ns: "common", defaultValue: "Gérer les filtres" })}
                    aria-label={t("dlgCriteriasManageFilterTitle", { ns: "common", defaultValue: "Gérer les filtres" })}
                  >
                    {/* BL-0427 — signet pour presets ; entonnoir réservé aux critères */}
                    <FaBookmark className="text-sm" aria-hidden />
                  </button>

                  <button
                    className={`flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-200 ease-in-out
                          focus:outline-hidden focus:ring-2 focus:ring-blue-500/20
                          active:scale-95
                          ${disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:bg-gray-100 active:bg-gray-200"}`}
                    onClick={handleSaveFilter}
                    disabled={disabled}
                    title="Sauvegarder le filtre"
                    aria-label="Sauvegarder le filtre"
                  >
                    <FaSave className="text-sm" />
                  </button>

                  <button
                    className={`flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-200 ease-in-out
                          focus:outline-hidden focus:ring-2 focus:ring-blue-500/20
                          active:scale-95
                          ${disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:bg-gray-100 active:bg-gray-200"}`}
                    onClick={() => {
                      doClearCriterias();
                    }}
                    disabled={disabled}
                    title="Effacer tous les critères"
                    aria-label="Effacer tous les critères"
                  >
                    <FaTrash className="text-sm" />
                  </button>
                </div>
              );
            })()}

            <div className="flex items-center flex-wrap min-w-0">
              {sortedSavedFilters.length > 0 && (
                <>
                  <div className="mx-3 h-6 w-px bg-gray-300 shrink-0" aria-hidden />
                  {savedFilterChips}
                </>
              )}
            </div>

            <div className="ml-auto shrink-0">
              <button
                type="button"
                className="flex items-center justify-center h-9 w-9 text-gray-500 hover:text-gray-700
                     rounded-lg transition-all duration-200 ease-in-out
                     hover:bg-gray-100 focus:bg-gray-100
                     focus:outline-hidden focus:ring-2 focus:ring-blue-500/20
                     active:bg-gray-200 active:scale-95"
                onClick={() => {
                  handleShowSearch(activeTab);
                }}
                aria-expanded={true}
                aria-controls={searchSectionId}
                title={t("searchPanelCollapseAria")}
                aria-label={t("searchPanelCollapseAria")}
              >
                <span className="text-lg font-medium" aria-hidden>
                  ✕
                </span>
              </button>
            </div>
          </div>

          {myTabs.length > 1 ? (
            <U2000Tab tabs={myTabs} defaultTab={defaultTab} activeTab={activeTab} setActiveTab={handleTabChange} />
          ) : (
            myTabs[0]?.content ?? null
          )}

          <div className="mt-6 flex items-center justify-end">
            <U2000ActionBtn label={t("btnSearch")} icon={FaSearch} handleClick={handleSearchClick} trailingHint={searchBtnTrailingHint} />
          </div>
        </>
      )}
    </>
  );
};

export default U2000BasicSearch;
